import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { areArrayEqual } from 'shared/utils';
import { validateUploadFile } from 'shared/utils/check-file-size';
import { BehaviorSubject, combineLatest, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { toUploadFileList } from 'shared/utils/to-upload-file-list';
import { AttachmentCategory } from 'shared/enums';
import * as download from 'downloadjs';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadChangeParam, NzUploadComponent, NzUploadFile } from 'ng-zorro-antd/upload';


@Component({
    selector: 'wx-attachment-input',
    templateUrl: './attachment-input.component.html',
    styleUrls: ['./attachment-input.component.scss', './attachment-input.component.dark.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AttachmentInputComponent),
            multi: true
        }
    ]
})
export class AttachmentInputComponent implements OnInit, ControlValueAccessor {
    attachmentSize: number;
    multiple$ = new BehaviorSubject<boolean>(false);

    limit$ = new BehaviorSubject<number>(10);
    disabled$ = new BehaviorSubject<boolean>(false);
    category$ = new ReplaySubject<AttachmentCategory>(1);
    acceptImage$ = new BehaviorSubject<boolean>(true);
    acceptVideo$ = new BehaviorSubject<boolean>(true);
    acceptDoc$ = new BehaviorSubject<boolean>(false);

    files$ = new BehaviorSubject<NzUploadFile[]>([]);
    fileNames$ = this.files$.pipe(
      map(files => files.map(f => f.name))
    );
    @Output() fileChange: EventEmitter<any> = new EventEmitter<any>();
    _writedValue: any;
    uploadUrl = 'api/attachment';

    // @Input()
    // set category(_: AttachmentCategory) {
    //   this.category$.next(_);
    // }
    canUpload$ = combineLatest(
      this.files$,
      this.multiple$,
      this.limit$,
      this.disabled$
    ).pipe(
      map(([files, multiple, limit, disabled]) => {
          if (disabled) {
              return false;
          }
          if (!multiple && files.length >= 1) {
              return false;
          }
          if (files.length >= limit) {
              return false;
          }
          return true;
      }),
      distinctUntilChanged()
    );
    nzShowUploadList$ = this.disabled$.pipe(
      map(disabled => {

          return { showPreviewIcon: true, showRemoveIcon: !disabled, showDownloadIcon: true };

      })
    );
    accept$ = combineLatest(
      this.acceptImage$,
      this.acceptVideo$,
      this.acceptDoc$
    ).pipe(
      map(([acceptImage, acceptVideo, acceptDoc]) => {
          const accepts = [];

          if (acceptImage) {
              accepts.push('.png');
              accepts.push('.jpg');
              accepts.push('.jpeg');
          }
          if (acceptVideo) {
              accepts.push('.mp4');
          }
          if (acceptDoc) {
              accepts.push('.doc');
              accepts.push('.docx');
              accepts.push('.xls');
              accepts.push('.xlsx');
              accepts.push('.pdf');

          }
          return accepts.join(',');

      })
    );
    @ViewChild(NzUploadComponent, { static: true })
    uploader: NzUploadComponent;
    onChange: (_: any) => void;
    onTouched: () => void;

    constructor(
      private messageService: NzMessageService,
      private httpClient: HttpClient,
      private translate: TranslateService
    ) {

    }

    @Input()
    set multiple(_: boolean) {
        this.multiple$.next(_);
    }

    @Input()
    set size(size: number) {
        this.attachmentSize = size;
    }

    @Input()
    set acceptImage(_: boolean) {
        this.acceptImage$.next(_);

    }

    @Input()
    set acceptVideo(_: boolean) {
        this.acceptVideo$.next(_);
    }

    @Input()
    set acceptDoc(_: boolean) {
        this.acceptDoc$.next(_);
    }

    // uploadUrl$ = this.category$.pipe(
    //   map(catgory => uploadUrl(catgory))
    // )

    get limit() {
        return this.limit$.value;
    }

    @Input()
    set limit(_: number) {
        this.limit$.next(_);
    }

    @Input()
    set fileNames(_: string[]) {

        this.writeValue(_);

    }

    @Input()
    set disabled(_: boolean) {
        this.setDisabledState(_);
    }

    previewFile = (file: NzUploadFile) => {
        return file.thumbUrl;
    };

    ngOnInit(): void {

        this.fileNames$.pipe(
          distinctUntilChanged((x, y) => areArrayEqual(x, y)),
          tap(fileNames => {
              if ((fileNames || []).length == 0) {
                  this.uploader.nzFileList = undefined;
              }

          }),

          switchMap(fileNames => this.multiple$.pipe(map(multiple => {
              return {
                  fileNames,
                  multiple
              };
          }))),
          filter(x => {
              let notifyChanged = false;
              if (x.multiple) {
                  notifyChanged = !areArrayEqual(x.fileNames, this._writedValue);
              } else {
                  notifyChanged = x.fileNames[0] != this._writedValue;
              }
              return notifyChanged;

          }),
        ).subscribe(x => {
            if (this.onChange) {

                if (x.multiple) {
                    this.onChange(x.fileNames);
                } else {
                    this.onChange(x.fileNames[0]);
                }

            }
        });
    }

    isImage = _ => true;

    writeValue(obj: any): void {
        this._writedValue = obj;
        if (obj == undefined) {
            this.files$.next([]);

        } else if (typeof obj === 'string') {
            this.files$.next(toUploadFileList([obj]));
        } else if (Array.isArray(obj)) {
            this.files$.next(toUploadFileList(obj));
        }

    }

    registerOnChange(fn: any): void {

        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {

        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled$.next(isDisabled);

    }

    // files: UploadFile[];

    download = (file: NzUploadFile) => {
        this.httpClient.get(`api/attachment/${file.name}/original-file-name`).pipe(
          // switchMap((name: string) => this.httpClient.get(file.url).pipe(map(content => { return { name, content } }))),
          tap((name: string) => {
              var url = new URL(file.url, window.location.origin);
              url.searchParams.append('fileDownloadName', name);
              download(url.toString());
          })
        ).subscribe();

    };

    uploadChange(e: NzUploadChangeParam) {
        if (e.file.status == 'done') {
            e.fileList.filter(f => f.uid == e.file.uid).forEach(f => Object.assign(f, e.file.response));
            this.files$.next(e.fileList);

        }
    }

    fileListChange(files: NzUploadFile[]) {
        this.files$.next(files || []);
        this.fileChange.emit(files);

    }

    beforeUpload = (file: NzUploadFile, fileList: NzUploadFile[]) => {

        if (fileList.length > this.limit) {

            return false;
        }
        const result = validateUploadFile(file, this.attachmentSize);
        if (!result.valid) {
            return this.translate.get('Common.AttachmentTooLarge').pipe(
              map(message => message.replace('{0}', (this.attachmentSize / 1024 / 1024).toString())),
              tap(message => this.messageService.error(message)),
              map(() => false)
            );
            // message = message.replace("{0}", (this.attachmentSize / 1024 / 1024).toString())

            // this.messageService.error(message);
        }
        return of(result.valid);

    };


}
